/* eslint-disable jira/import-whitelist */
// eoc-fetch is already being used in eoc resources, so there is no reason not
// to override the whitelist here.
import { eocFetch as fetch } from '@atlassian/eoc-fetch/controllers';
import { fg } from '@atlassian/jira-feature-gating';
import {
	checkIsTransitioned,
	checkIsTransitioning,
} from '@jira/eoc__tenant-info/src/controllers/utils.tsx';
import { createResource, type RouteResource } from '@atlassian/react-resource-router';

const ConsolidationState = {
	NOT_CONSOLIDATED: 'NOT_CONSOLIDATED',
	TRANSITIONING: 'TRANSITIONING',
	TRANSITIONED: 'TRANSITIONED',
	CONSOLIDATED: 'CONSOLIDATED',
} as const;

type ConsolidationState = (typeof ConsolidationState)[keyof typeof ConsolidationState];

type Cohort = 'cohort1' | 'cohort2' | 'cohort3' | 'cohort3.5' | 'cohort4';

interface TenantInfo {
	consolidationState: ConsolidationState;
	cohortInfo?: Cohort | null;
	isAdmin?: boolean;
}

const CONSOLIDATION_STATE_RESOURCE_TYPE = 'EOC_TENANT';

type ApiCheckResponse = {
	data: boolean;
};

const CHECK_API_KEY_RESOURCE_TYPE = 'EOC_CHECK_API_KEY';

const fetchCheckApiKeyManagementKeys = async (
	accountId: string | null,
	isSiteAdmin: boolean,
	opsEnabled: boolean,
	appPermissions?: {
		hasServiceDeskAccess: boolean;
	},
): Promise<boolean> => {
	if (
		!opsEnabled ||
		!isSiteAdmin ||
		(appPermissions && appPermissions.hasServiceDeskAccess === false)
	) {
		return Promise.resolve(false);
	}

	return fetch<ApiCheckResponse>(`/api-keys/web/check?accountId=${accountId}`).then(
		({ data }) => data,
	);
};

// eslint-disable-next-line jira/router/no-daisy-chaining-resources
const resource = createResource<boolean>({
	type: CHECK_API_KEY_RESOURCE_TYPE,
	getKey: () => 'check-api-key-management-keys',
	getData: async (
		{ dependencies },
		{ tenantContext: { atlassianAccountId, appPermissions, isSiteAdmin } },
	) => {
		// oxlint-disable-next-line typescript/consistent-type-assertions
		const tenantInfo = (await dependencies[CONSOLIDATION_STATE_RESOURCE_TYPE]
			?.promise) as TenantInfo;
		const opsEnabled =
			checkIsTransitioning(tenantInfo) ||
			(checkIsTransitioned(tenantInfo) && fg('api_key_management_open_for_transitioned_users'));

		return fetchCheckApiKeyManagementKeys(
			atlassianAccountId,
			isSiteAdmin,
			opsEnabled,
			appPermissions,
		);
	},
	maxAge: Infinity,
	isBrowserOnly: true,
	depends: [CONSOLIDATION_STATE_RESOURCE_TYPE],
	isCritical: true,
});

export const checkApiKeyManagementKeysResource = (): RouteResource<boolean> => resource;
